import {useEffect, useMemo, useRef} from 'react';

export const useProductData = ({
	                               isInitialSyncComplete,
	                               filters,
	                               pagination,
	                               catrefid,
	                               subcatrefid,
	                               brandName,
	                               getAllProducts,
	                               getAllProductsFiltersOption,
	                               text,
	                               barcode,
	                               imageId,
	                               slug
                               }) => {
	// Add refs to prevent rapid duplicate fetches (race condition guard)
	const isFetchingRef = useRef(false);
	const lastFetchParamsRef = useRef(null);
	const isFetchingFiltersRef = useRef(false);
	const lastFilterParamsRef = useRef(null);

	// Memoize additional params for products
	const productAdditionalParams = useMemo(() => {
		if (catrefid !== "best-deals") return {};

		const dealTypes = {
			'new-arrivals': {new_arrival: 1, category: '', subcategory: ''},
			'promotions': {promotion: 1, category: '', subcategory: ''},
			'best-seller': {best_seller: 1, category: '', subcategory: ''},
		};

		if (dealTypes[subcatrefid]) {
			return dealTypes[subcatrefid];
		}

		if (subcatrefid) {
			return {category: '', subcategory: subcatrefid};
		}

		return {trending: 1, category: '', subcategory: ''};
	}, [catrefid, subcatrefid]);

	// Memoize additional params for filters
	const filterAdditionalParams = useMemo(() => {
		if (catrefid !== "best-deals") return {};

		const dealTypes = {
			'new-arrivals': {new_arrival: 1, category: '', subcategory: ''},
			'trending': {trending: 1, category: '', subcategory: ''},
			'promotions': {promotion: 1, category: '', subcategory: ''},
			'best-seller': {best_seller: 1, category: '', subcategory: ''},
		};

		if (dealTypes[subcatrefid]) {
			return dealTypes[subcatrefid];
		}

		if (subcatrefid) {
			return {category: '', subcategory: subcatrefid};
		}

		return {};
	}, [catrefid, subcatrefid]);

	// Fetch products
	useEffect(() => {
		if (!isInitialSyncComplete) return;

		const queryParams = {
			page: pagination?.currentPage || 1,
			limit: pagination?.itemsPerPage || 20,
			q: filters?.q || text || '',
      category: catrefid == "best-deals" ? "" : catrefid || '',
      subcategory: catrefid == "best-deals" ? "" : subcatrefid || '',
			brand: brandName || '',
			cat_id: filters?.cat_id,
			sub_cat_id: filters?.sub_cat_id,
			brand_id: filters?.brand_id,
			country_id: filters?.country_id,
			diet_id: filters?.diet_id,
			min: filters?.min > 0 ? filters.min : undefined,
			max: filters?.max > 0 && filters.max < 1000 ? filters.max : undefined,
			promotion: filters?.promotion ? 1 : undefined,
			oos: filters?.oos ? 1 : undefined,
			sort: filters?.sort !== 'a-z' ? filters.sort : undefined,
			store_id: 1,
			append: (pagination?.currentPage || 1) > 1,
			// Advanced search parameters for V4 search
			barcode: barcode || filters?.barcode || undefined,
			imageId: imageId || filters?.imageId || undefined,
			pagetype: 'web',
			// Selection page slug for landing/partner pages
			slug: slug || undefined,
			...productAdditionalParams,
		};

		// Create a unique key for these params to prevent duplicate fetches
		const paramsKey = JSON.stringify({
			page: queryParams.page,
			filters: {
				cat_id: queryParams.cat_id,
				sub_cat_id: queryParams.sub_cat_id,
				brand_id: queryParams.brand_id,
				country_id: queryParams.country_id,
				diet_id: queryParams.diet_id,
				min: queryParams.min,
				max: queryParams.max,
				promotion: queryParams.promotion,
				oos: queryParams.oos,
				sort: queryParams.sort,
				q: queryParams.q,
			},
      catrefid: catrefid === "best-deals" ? "" : catrefid || '',
      subcatrefid: catrefid === "best-deals" ? "" : subcatrefid || '',
			brandName,
		});

		// Prevent duplicate fetches with same params (race condition guard)
		if (isFetchingRef.current || lastFetchParamsRef.current === paramsKey) {
			return;
		}

		isFetchingRef.current = true;
		lastFetchParamsRef.current = paramsKey;

		getAllProducts(queryParams);

		// Reset fetching flag after a short delay
		const timer = setTimeout(() => {
			isFetchingRef.current = false;
		}, 500);

		return () => clearTimeout(timer);
	}, [
		isInitialSyncComplete,
		pagination?.currentPage,
		pagination?.itemsPerPage,
		filters?.q,
		filters?.cat_id?.join(','),  // Convert array to string for stable comparison
		filters?.sub_cat_id?.join(','),  // Convert array to string for stable comparison
		filters?.brand_id?.join(','),  // Convert array to string for stable comparison
		filters?.country_id?.join(','),  // Convert array to string for stable comparison
		filters?.diet_id?.join(','),  // Convert array to string for stable comparison
		filters?.min,
		filters?.max,
		filters?.promotion,
		filters?.oos,
		filters?.sort,
		filters?.barcode,
		filters?.imageId,
		catrefid,
		subcatrefid,
		brandName,
		text,
		barcode,
		imageId,
		slug,
		JSON.stringify(productAdditionalParams),  // Stringify object for stable comparison
		// Removed getAllProducts - function reference changes don't need to trigger re-fetch
	]);

	// Fetch filter options
	useEffect(() => {
		const queryParams = {
			store_id: 1,
			q: filters?.q || text || '',
			...filterAdditionalParams,
		};

		// If on a selection page, only send slug
		if (slug) {
			queryParams.slug = slug;
		} else {
			// Otherwise send category/subcategory/brand
      queryParams.category = catrefid === "best-deals" ? "" : catrefid || '';
      queryParams.subcategory = catrefid === "best-deals" ? "" : subcatrefid || '';
			queryParams.brand = brandName || '';
		}

		// Create unique key to prevent duplicate filter fetches
		const filterParamsKey = JSON.stringify({
			catrefid,
			subcatrefid,
			brandName,
			slug,
			q: filters?.q || text,
		});

		// Prevent duplicate filter option fetches
		if (isFetchingFiltersRef.current || lastFilterParamsRef.current === filterParamsKey) {
			return;
		}

		isFetchingFiltersRef.current = true;
		lastFilterParamsRef.current = filterParamsKey;

		getAllProductsFiltersOption(queryParams);

		// Reset flag after delay
		const timer = setTimeout(() => {
			isFetchingFiltersRef.current = false;
		}, 300);

		return () => clearTimeout(timer);
	}, [
		catrefid,
		subcatrefid,
		brandName,
		slug,
		filters?.q,
		text,
		JSON.stringify(filterAdditionalParams),  // Stringify object for stable comparison
		// Removed getAllProductsFiltersOption - function reference changes don't need to trigger re-fetch
	]);
};